import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { io } from "socket.io-client";

function readStoredActiveGame() {
  try {
    const saved = localStorage.getItem("activeGame");
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
}

/**
 * Realtime head-to-head games over `/games` (Socket.IO).
 * Mirrors the existing FloatingChat wiring; use from the dedicated Messages UI.
 */
export default function useRealtimeGames({ currentUserId, socketOrigin }) {
  const gameSocketRef = useRef(null);
  const [gameChannelReady, setGameChannelReady] = useState(false);
  const [isGameInviteOpen, setIsGameInviteOpen] = useState(false);
  const [activeGame, setActiveGame] = useState(readStoredActiveGame);
  const [pendingInvite, setPendingInvite] = useState(null);

  useEffect(() => {
    if (!currentUserId) {
      setGameChannelReady(false);
      return undefined;
    }

    const gameSocket = io(`${socketOrigin}/games`, {
      withCredentials: true,
    });

    gameSocketRef.current = gameSocket;

    const onConnect = () => setGameChannelReady(true);
    const onDisconnect = () => setGameChannelReady(false);

    gameSocket.on("connect", onConnect);
    gameSocket.on("disconnect", onDisconnect);

    gameSocket.on("game-invited", ({ gameId, gameType, inviterId, inviterName }) => {
      setPendingInvite({
        gameId,
        gameType,
        opponentId: inviterId,
        opponentName: inviterName,
      });
    });

    gameSocket.on("game-started", ({ gameId, gameType, opponentId, opponentName, gameState }) => {
      setPendingInvite(null);
      const game = { gameId, gameType, opponentId, opponentName, gameState };
      setActiveGame(game);
      try {
        localStorage.setItem("activeGame", JSON.stringify(game));
      } catch (e) {
        console.error("[Games] Failed to save active game:", e);
      }
    });

    gameSocket.on("game-move-success", ({ gameState }) => {
      setActiveGame((prev) => {
        if (!prev) return null;
        const updated = { ...prev, gameState };
        try {
          localStorage.setItem("activeGame", JSON.stringify(updated));
        } catch (e) {
          console.error("[Games] Failed to update active game:", e);
        }
        return updated;
      });
    });

    gameSocket.on("game-ended", ({ gameState }) => {
      setActiveGame((prev) => {
        if (!prev) return null;
        return { ...prev, gameState };
      });
    });

    gameSocket.on("game-error", ({ error }) => {
      console.error("[Games] Socket error:", error);
      window.alert(`Game error: ${error}`);
    });

    gameSocket.on("game-declined", () => {
      setPendingInvite(null);
    });

    return () => {
      gameSocket.off("connect", onConnect);
      gameSocket.off("disconnect", onDisconnect);
      gameSocket.disconnect();
      gameSocketRef.current = null;
      setGameChannelReady(false);
    };
  }, [currentUserId, socketOrigin]);

  const sendGameInvite = useCallback((friendUserId, gameType) => {
    const id = Number(friendUserId);
    if (!Number.isFinite(id) || !gameSocketRef.current) return;
    gameSocketRef.current.emit("game-invite", { recipientId: id, gameType });
    setIsGameInviteOpen(false);
  }, []);

  const acceptGameInvite = useCallback(() => {
    if (!pendingInvite || !gameSocketRef.current) return;
    gameSocketRef.current.emit("game-accept", { gameId: pendingInvite.gameId });
  }, [pendingInvite]);

  const declineGameInvite = useCallback(() => {
    if (!pendingInvite || !gameSocketRef.current) return;
    gameSocketRef.current.emit("game-decline", { gameId: pendingInvite.gameId });
    setPendingInvite(null);
  }, [pendingInvite]);

  const submitGameMove = useCallback(
    (move) => {
      if (!activeGame || !gameSocketRef.current) return;
      gameSocketRef.current.emit("game-move", {
        gameId: activeGame.gameId,
        move,
      });
    },
    [activeGame]
  );

  const forfeitActiveGame = useCallback(() => {
    if (!activeGame || !gameSocketRef.current) return;
    const confirmed = window.confirm("Are you sure you want to forfeit this game?");
    if (!confirmed) return;
    gameSocketRef.current.emit("game-forfeit", { gameId: activeGame.gameId });
    try {
      localStorage.removeItem("activeGame");
    } catch (e) {
      console.error("[Games] Failed to clear active game:", e);
    }
  }, [activeGame]);

  const dismissActiveGame = useCallback(() => {
    setActiveGame(null);
    try {
      localStorage.removeItem("activeGame");
    } catch (e) {
      console.error("[Games] Failed to clear active game:", e);
    }
  }, []);

  return useMemo(
    () => ({
      gameChannelReady,
      isGameInviteOpen,
      setIsGameInviteOpen,
      activeGame,
      pendingInvite,
      sendGameInvite,
      acceptGameInvite,
      declineGameInvite,
      submitGameMove,
      forfeitActiveGame,
      dismissActiveGame,
    }),
    [
      gameChannelReady,
      isGameInviteOpen,
      activeGame,
      pendingInvite,
      sendGameInvite,
      acceptGameInvite,
      declineGameInvite,
      submitGameMove,
      forfeitActiveGame,
      dismissActiveGame,
    ]
  );
}
